import React from 'react';
import { Avatar, Flex } from 'antd';
import { faUsers } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useParams } from 'react-router-dom';

import { RosterClient } from 'jf/api';
import { useDevExTheme } from 'jf/common/themes/DevExTheme';
import { DevExDrawer } from 'jf/components/DevExDrawer';
import { useClientQuery } from 'jf/utils/useClientQuery';

export enum TargetType {
  SlackUser,
  MSTeamsUser,
}

type RespondentDetailsDrawerProps = {
  onClose: () => void;
  open: boolean;
  targetType: TargetType;
  email: string;
  realName?: string;
  userName?: string;
  profilePhotoSrc?: any; // string or <img>
};

export const RespondentDetailsDrawers: React.FC<RespondentDetailsDrawerProps> = ({
  onClose,
  open,
  targetType,
  email,
  realName,
  userName,
  profilePhotoSrc,
}) => {
  const theme = useDevExTheme();
  const { studyRef } = useParams<{ studyRef: string }>();
  const infoBlock = (title, information) => {
    return (
      <Flex vertical gap={theme.variable.spacing.sm}>
        <div style={{ color: theme.color.text.secondary }}> {title} </div>
        <div> {information} </div>
      </Flex>
    );
  };

  const { data: rosterUsers } = useClientQuery(RosterClient.getRosterTargets, {
    studyRef,
    slackSelected: targetType === TargetType.SlackUser,
    msteamsSelected: targetType === TargetType.MSTeamsUser,
  });
  const rosterUser = rosterUsers?.find((user) => user.email === email);
  const msgProvider = targetType === TargetType.SlackUser ? 'Slack' : 'MS Teams';

  return (
    <DevExDrawer
      title={realName ? realName : userName}
      open={open}
      onClose={() => {
        onClose();
      }}
      closable
    >
      <Flex vertical gap={theme.variable.spacing.md}>
        <strong style={{ fontSize: theme.variable.fontSize.md }}> {msgProvider} Profile </strong>
        <Flex gap={theme.variable.spacing.md}>
          <Avatar
            shape={'square'}
            style={{ backgroundColor: theme.color.background.active }}
            size="large"
            src={profilePhotoSrc ?? <FontAwesomeIcon icon={faUsers} />}
          />
          <Flex vertical gap={theme.variable.spacing.md}>
            {infoBlock('Email', email)}
            {!!realName && infoBlock('Name', realName)}
            {infoBlock(`${msgProvider} Username`, `@` + userName)}
          </Flex>
        </Flex>
        {rosterUser && (
          <>
            <strong
              style={{
                fontSize: theme.variable.fontSize.md,
                paddingTop: theme.variable.spacing.sm,
              }}
            >
              Jellyfish Profile
            </strong>
            <Flex gap={theme.variable.spacing.md}>
              <Avatar
                shape={'square'}
                style={{ backgroundColor: theme.color.background.active }}
                size="large"
                src={<FontAwesomeIcon icon={faUsers} />}
              />
              <Flex vertical gap={theme.variable.spacing.md}>
                {infoBlock('Email', rosterUser.email)}
                {infoBlock('Name', rosterUser.name)}
                {Object.entries(rosterUser.segmentValueAssignment).map(([key, value]) =>
                  infoBlock(key, value)
                )}
              </Flex>
            </Flex>
          </>
        )}
      </Flex>
    </DevExDrawer>
  );
};
